import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageLayout } from "../components/PageLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { loadDiagrams, deleteDiagram, renameDiagram } from "../data/diagrams";

import type { DiagramEntry } from "../data/diagrams";

const VaultPage: React.FC = () => {
  const [diagrams, setDiagrams] = useState<DiagramEntry[]>([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("created-desc");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setDiagrams(loadDiagrams());
  }, []);

  const handleDelete = (id: string) => {
    deleteDiagram(id);
    setDiagrams(loadDiagrams());
  };

  const startEditing = (diagram: DiagramEntry) => {
    setEditingId(diagram.id);
    setEditingName(diagram.name);
  };

  const handleRename = (id: string) => {
    renameDiagram(id, editingName);
    setDiagrams(loadDiagrams());
    setEditingId(null);
  };

  const filteredDiagrams = useMemo(() => {
    return diagrams
      .filter((d) => d.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        switch (sort) {
          case "name-asc":
            return a.name.localeCompare(b.name);
          case "name-desc":
            return b.name.localeCompare(a.name);
          case "created-asc":
            return a.created - b.created;
          default:
            return b.created - a.created;
        }
      });
  }, [diagrams, search, sort]);

  return (
    <PageLayout>
      <div className="container py-8 min-h-[calc(100vh-10rem)]">
        <h1 className="text-2xl font-bold mb-4">Library</h1>
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="sm:w-64"
          />
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="sm:w-40">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="created-desc">Newest</SelectItem>
              <SelectItem value="created-asc">Oldest</SelectItem>
              <SelectItem value="name-asc">Name A-Z</SelectItem>
              <SelectItem value="name-desc">Name Z-A</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {filteredDiagrams.length === 0 ? (
          <p>No diagrams saved.</p>
        ) : (
          <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
            {filteredDiagrams.map((d) => (
              <div key={d.id} className="border rounded p-2 flex flex-col">
                <img
                  src={d.thumbnail}
                  alt={d.name}
                  className="mb-2 h-40 object-contain bg-white"
                />
                {editingId === d.id ? (
                  <Input
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    className="flex-grow mb-2"
                  />
                ) : (
                  <span className="flex-grow mb-2">{d.name}</span>
                )}
                <div className="flex gap-2 mt-2">
                  {editingId === d.id ? (
                    <>
                      <Button size="sm" onClick={() => handleRename(d.id)}>
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setEditingId(null)}
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        onClick={() =>
                          navigate("/editor", {
                            state: {
                              id: d.id,
                              name: d.name,
                              shapes: d.elements,
                            },
                          })
                        }
                      >
                        Open
                      </Button>
                      <Button size="sm" onClick={() => startEditing(d)}>
                        Rename
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(d.id)}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
};

export default VaultPage;
